// Version: 1.5
// v1.5: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.4: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';
import { BulkImportModal } from './AssetInventory';

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'device',           label: 'Device' },
  { id: 'network',          label: 'Network' },
  { id: 'domain',           label: 'Domain' },
  { id: 'certificate',      label: 'Certificate' },
  { id: 'm365_licence',     label: 'M365 Licence' },
  { id: 'software_licence', label: 'Software Licence' },
  { id: 'subscription',     label: 'Subscription' },
  { id: 'vendor_contract',  label: 'Vendor Contract' },
  { id: 'cyber_insurance',  label: 'Cyber Insurance' },
  { id: 'warranty',         label: 'Warranty' },
];

const STATUSES = ['active','inactive','decommissioned','in_storage','lost_stolen'];

const STATUS_LABELS = {
  active:          'Active',
  inactive:        'Inactive',
  decommissioned:  'Decommissioned',
  in_storage:      'In Storage',
  lost_stolen:     'Lost / Stolen',
};

function statusLabel(status) {
  return STATUS_LABELS[status] || (status || '').replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());
}

const CAT_ICONS = {
  device:           '',
  network:          '',
  domain:           '',
  certificate:      '',
  m365_licence:     '☁️',
  software_licence: '',
  subscription:     '',
  vendor_contract:  '',
  cyber_insurance:  '️',
  warranty:         '✅',
};

const RAG_COLOURS = {
  expired: '#ff2d55',
  red:     '#ff2d55',
  amber:   '#e6b800',
  yellow:  '#e6b800',
  green:   '#30d158',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}

function fmtCost(cost, currency) {
  if (!cost) return '—';
  const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';
  return `${sym}${parseFloat(cost).toFixed(2)}`;
}

function categoryLabel(id) {
  return CATEGORIES.find(c => c.id === id)?.label || id;
}

function ExpiryBadge({ asset }) {
  const { expiry_date, days_until_expiry, expiry_rag } = asset;
  if (!expiry_date) return <span style={{color:'var(--text-muted)',fontSize:11}}>No expiry</span>;
  const colour = RAG_COLOURS[expiry_rag] || 'var(--text-muted)';
  const label  = days_until_expiry < 0
    ? 'Expired'
    : days_until_expiry === 0
    ? 'Today'
    : `${days_until_expiry}d`;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
      <span style={{
        width:8, height:8, borderRadius:'50%', background:colour, flexShrink:0, display:'inline-block'
      }}/>
      <span style={{fontSize:12, color:colour, fontWeight:600}}>{label}</span>
      <span style={{fontSize:11, color:'var(--text-muted)'}}>{fmtDate(expiry_date)}</span>
    </span>
  );
}

// ── Asset Form Modal ───────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name:'', category:'device', vendor:'', status:'active', expiry_date:'',
  auto_renew:false, visible_to_client:false, cost:'', currency:'GBP',
  notes:'', type:'', make:'', model:'', serial:'', os:'', ip:'', mac:'',
  hostname:'', site:'', owner_name:'', owner_email:'', owner_dept:'',
  asset_tag:'', purchase_date:'', warranty_expiry:'', source:'manual',
};

function AssetFormModal({ slug, asset, onSaved, onClose }) {
  const isMobile = useIsMobile();
  const [form,    setForm]    = useState(asset ? { ...EMPTY_FORM, ...asset } : { ...EMPTY_FORM });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [section, setSection] = useState('core');

  const isDevice = ['device','network'].includes(form.category);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const method = asset ? 'PUT' : 'POST';
      const url    = asset
        ? `/api/clients/${slug}/assets/${asset.id}`
        : `/api/clients/${slug}/assets`;
      const res  = await authFetch(url, { method, headers:{'Content-Type':'application/json'}, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed'); setSaving(false); return; }
      onSaved(data);
    } catch(e) { setError(String(e)); setSaving(false); }
  }

  const SECTIONS = [
    { id:'core',     label:'Core' },
    { id:'expiry',   label:'Expiry and Cost' },
    ...(isDevice ? [{ id:'device', label:'Device Detail' }] : []),
    { id:'owner',    label:'Owner' },
    { id:'notes',    label:'Notes' },
  ];

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',borderRadius:12,padding:24,width:'100%',maxWidth:560,maxHeight:'90vh',overflowY:'auto',border:'1px solid var(--border)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:16}}>{asset ? 'Edit Asset' : 'Add Asset'}</div>
          <button className="btn btn-quiet btn-icon" onClick={onClose}>✕</button>
        </div>

        {/* Section tabs */}
        <div style={{display:'flex',gap:2,borderBottom:'1px solid var(--border)',marginBottom:16,flexWrap: isMobile ? 'wrap' : 'nowrap',overflowX: isMobile ? 'visible' : 'auto',scrollbarWidth:'none'}}>
          {SECTIONS.map(s => (
            <button className="tab-btn" aria-selected={section===s.id} key={s.id} onClick={() => setSection(s.id)}>{s.label}</button>
          ))}
        </div>

        {section === 'core' && (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <Field label="Name *">
              <input className="input-sm" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Microsoft 365 Business Premium" style={inp}/>
            </Field>
            <Field label="Category">
              <select className="input-sm" value={form.category} onChange={e=>set('category',e.target.value)} style={inp}>
                {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Vendor / Registrar">
              <input className="input-sm" value={form.vendor||''} onChange={e=>set('vendor',e.target.value)} placeholder="e.g. Microsoft, Cloudflare, Dell" style={inp}/>
            </Field>
            <Field label="Status">
              <select className="input-sm" value={form.status} onChange={e=>set('status',e.target.value)} style={inp}>
                {STATUSES.map(s=><option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
            </Field>
            <Field label="Visible to client">
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                <input type="checkbox" checked={!!form.visible_to_client} onChange={e=>set('visible_to_client',e.target.checked)} style={{width:14,height:14,accentColor:'var(--accent)'}}/>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>Show this asset in the client portal</span>
              </label>
            </Field>
          </div>
        )}

        {section === 'expiry' && (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <Field label="Expiry date">
              <input className="input-sm" type="date" value={form.expiry_date||''} onChange={e=>set('expiry_date',e.target.value)} style={inp}/>
            </Field>
            <Field label="Auto-renew">
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                <input type="checkbox" checked={!!form.auto_renew} onChange={e=>set('auto_renew',e.target.checked)} style={{width:14,height:14,accentColor:'var(--accent)'}}/>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>This asset renews automatically</span>
              </label>
            </Field>
            <Field label="Annual cost">
              <div style={{display:'flex',gap:8}}>
                <select className="input-sm" value={form.currency||'GBP'} onChange={e=>set('currency',e.target.value)} style={{...inp,width:80}}>
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input className="input-sm" value={form.cost||''} onChange={e=>set('cost',e.target.value)} placeholder="0.00" type="number" step="0.01" min="0" style={{...inp,flex:1}}/>
              </div>
            </Field>
            <Field label="Purchase date">
              <input className="input-sm" type="date" value={form.purchase_date||''} onChange={e=>set('purchase_date',e.target.value)} style={inp}/>
            </Field>
            <Field label="Warranty expiry">
              <input className="input-sm" type="date" value={form.warranty_expiry||''} onChange={e=>set('warranty_expiry',e.target.value)} style={inp}/>
            </Field>
          </div>
        )}

        {section === 'device' && isDevice && (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <Field label="Make"><input className="input-sm" value={form.make||''} onChange={e=>set('make',e.target.value)} style={inp}/></Field>
            <Field label="Model"><input className="input-sm" value={form.model||''} onChange={e=>set('model',e.target.value)} style={inp}/></Field>
            <Field label="Serial"><input className="input-sm" value={form.serial||''} onChange={e=>set('serial',e.target.value)} style={inp}/></Field>
            <Field label="OS"><input className="input-sm" value={form.os||''} onChange={e=>set('os',e.target.value)} style={inp}/></Field>
            <Field label="IP address"><input className="input-sm" value={form.ip||''} onChange={e=>set('ip',e.target.value)} style={inp}/></Field>
            <Field label="MAC address"><input className="input-sm" value={form.mac||''} onChange={e=>set('mac',e.target.value)} style={inp}/></Field>
            <Field label="Hostname"><input className="input-sm" value={form.hostname||''} onChange={e=>set('hostname',e.target.value)} style={inp}/></Field>
            <Field label="Site"><input className="input-sm" value={form.site||''} onChange={e=>set('site',e.target.value)} style={inp}/></Field>
            <Field label="Asset tag"><input className="input-sm" value={form.asset_tag||''} onChange={e=>set('asset_tag',e.target.value)} style={inp}/></Field>
          </div>
        )}

        {section === 'owner' && (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <Field label="Owner name"><input className="input-sm" value={form.owner_name||''} onChange={e=>set('owner_name',e.target.value)} style={inp}/></Field>
            <Field label="Owner email"><input className="input-sm" value={form.owner_email||''} onChange={e=>set('owner_email',e.target.value)} style={inp}/></Field>
            <Field label="Department"><input className="input-sm" value={form.owner_dept||''} onChange={e=>set('owner_dept',e.target.value)} style={inp}/></Field>
          </div>
        )}

        {section === 'notes' && (
          <div>
            <textarea className="input-sm" value={form.notes||''} onChange={e=>set('notes',e.target.value)}
              placeholder="Any additional notes..." rows={6}
              style={{ ...inp, resize:'vertical' }}/>
          </div>
        )}

        {error && <div style={{marginTop:12,color:'#ff2d55',fontSize:12}}>{error}</div>}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:20}}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>{label}</div>
      {children}
    </div>
  );
}

const inp = { width:'100%' };

// ── Asset Row ─────────────────────────────────────────────────────────────────

function AssetRow({ asset, isAdmin, selected, onSelect, onEdit, onDelete, showClient }) {
  const isMobile = useIsMobile();
  const icon = CAT_ICONS[asset.category] || '';

  if (isMobile) {
    return (
      <div style={{
        padding: '12px 14px', borderBottom: '1px solid var(--border)',
        background: selected ? 'rgba(0,212,255,0.04)' : 'transparent',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          {isAdmin && (
            <input type="checkbox" checked={selected} onChange={e=>onSelect(e.target.checked)}
              style={{width:14,height:14,marginTop:3,accentColor:'var(--accent)',cursor:'pointer',flexShrink:0}}/>
          )}
          <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{fontWeight:600,fontSize:13,overflowWrap:'break-word'}}>{asset.name}</div>
            <div style={{color:'var(--text-muted)',fontSize:11,marginTop:1}}>
              {categoryLabel(asset.category)}
              {asset.vendor ? ` · ${asset.vendor}` : ''}
              {showClient && asset.client_display_name ? ` · ${asset.client_display_name}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: (isAdmin || true) ? 8 : 0 }}>
          <ExpiryBadge asset={asset}/>
          <span style={{color:'var(--text-muted)',fontSize:12}}>
            {asset.cost ? fmtCost(asset.cost, asset.currency) : '—'}
            {asset.auto_renew && <span style={{marginLeft:4,fontSize:10,color:'var(--accent)'}}>↻ auto</span>}
          </span>
          <span style={{
            padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:600,
            background: asset.status==='active' ? '#30d15820' : 'var(--border)',
            color: asset.status==='active' ? '#30d158' : 'var(--text-muted)',
          }}>{statusLabel(asset.status)}</span>
          {isAdmin && (
            <span style={{
              padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:600,
              background: asset.visible_to_client ? '#30d15820' : 'var(--border)',
              color: asset.visible_to_client ? '#30d158' : 'var(--text-muted)',
            }}>{asset.visible_to_client ? 'Visible' : 'Private'}</span>
          )}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={()=>onEdit(asset)}>Edit</button>
            <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={()=>onDelete(asset)}>Remove</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      display:'grid',
      gridTemplateColumns: isAdmin
        ? `28px 28px 1fr 120px 120px 120px 90px 80px 70px`
        : `28px 1fr 120px 120px 120px`,
      gap:8, alignItems:'center',
      padding:'10px 12px',
      borderBottom:'1px solid var(--border)',
      background: selected ? 'rgba(0,212,255,0.04)' : 'transparent',
      fontSize:12,
    }}>
      {isAdmin && (
        <input type="checkbox" checked={selected} onChange={e=>onSelect(e.target.checked)}
          style={{width:14,height:14,accentColor:'var(--accent)',cursor:'pointer'}}/>
      )}
      <span style={{fontSize:16,textAlign:'center'}}>{icon}</span>
      <div style={{minWidth:0}}>
        <div style={{fontWeight:600,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{asset.name}</div>
        <div style={{color:'var(--text-muted)',fontSize:11,marginTop:1}}>
          {categoryLabel(asset.category)}
          {asset.vendor ? ` · ${asset.vendor}` : ''}
          {showClient && asset.client_display_name ? ` · ${asset.client_display_name}` : ''}
        </div>
      </div>
      <div><ExpiryBadge asset={asset}/></div>
      <div style={{color:'var(--text-muted)'}}>
        {asset.cost ? fmtCost(asset.cost, asset.currency) : '—'}
        {asset.auto_renew && <span style={{marginLeft:4,fontSize:10,color:'var(--accent)'}}>↻</span>}
      </div>
      <div>
        <span style={{
          padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:600,
          background: asset.status==='active' ? '#30d15820' : 'var(--border)',
          color: asset.status==='active' ? '#30d158' : 'var(--text-muted)',
        }}>{statusLabel(asset.status)}</span>
      </div>
      {isAdmin && (
        <>
          <div>
            <span style={{
              padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:600,
              background: asset.visible_to_client ? '#30d15820' : 'var(--border)',
              color: asset.visible_to_client ? '#30d158' : 'var(--text-muted)',
            }}>{asset.visible_to_client ? 'Visible' : 'Private'}</span>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={()=>onEdit(asset)}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={()=>onDelete(asset)}>Remove</button>
        </>
      )}
    </div>
  );
}

// ── Column Header ─────────────────────────────────────────────────────────────

function ColHeader({ isAdmin, allSelected, onSelectAll }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
        borderBottom: '2px solid var(--border)', fontSize: 11, color: 'var(--text-muted)',
      }}>
        {isAdmin && (
          <input type="checkbox" checked={allSelected} onChange={e=>onSelectAll(e.target.checked)}
            style={{width:14,height:14,accentColor:'var(--accent)',cursor:'pointer'}}/>
        )}
        <span>{isAdmin ? 'Select all' : 'Assets'}</span>
      </div>
    );
  }
  return (
    <div style={{
      display:'grid',
      gridTemplateColumns: isAdmin
        ? `28px 28px 1fr 120px 120px 120px 90px 80px 70px`
        : `28px 1fr 120px 120px 120px`,
      gap:8, alignItems:'center',
      padding:'8px 12px',
      borderBottom:'2px solid var(--border)',
      fontSize:10, fontWeight:700, color:'var(--text-muted)',
      textTransform:'uppercase', letterSpacing:'0.06em',
    }}>
      {isAdmin && (
        <input type="checkbox" checked={allSelected} onChange={e=>onSelectAll(e.target.checked)}
          style={{width:14,height:14,accentColor:'var(--accent)',cursor:'pointer'}}/>
      )}
      <div/>
      <div>Asset</div>
      <div>Expiry</div>
      <div>Cost</div>
      <div>Status</div>
      {isAdmin && <><div>Visibility</div><div/><div/></>}
    </div>
  );
}

// ── Export helpers ────────────────────────────────────────────────────────────

function triggerDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
}

function exportPDF(assets, clientName, isClientScope, productName, accentColour) {
  const title = isClientScope ? `${clientName} — Asset Register` : 'Asset Register';
  const now   = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});

  const rows = assets.map(a => `
    <tr>
      <td>${CAT_ICONS[a.category]||''} ${categoryLabel(a.category)}</td>
      <td><strong>${a.name}</strong>${a.vendor?`<br><small>${a.vendor}</small>`:''}</td>
      <td>${a.expiry_date ? fmtDate(a.expiry_date) : '—'}</td>
      <td style="color:${RAG_COLOURS[a.expiry_rag]||'inherit'}">${
        a.days_until_expiry != null ? (a.days_until_expiry < 0 ? 'Expired' : `${a.days_until_expiry}d`) : '—'
      }</td>
      <td>${a.cost ? fmtCost(a.cost, a.currency) : '—'}</td>
      <td>${a.auto_renew ? 'Yes' : 'No'}</td>
      <td>${statusLabel(a.status)}</td>
      ${!isClientScope ? `<td>${a.client_display_name||a.client_slug||''}</td>` : ''}
      <td>${a.notes||''}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<title>${title}</title>
<style>
  @page { margin: 20mm; }
  body { font-family: -apple-system, Arial, sans-serif; font-size: 11px; color: #1a1a1a; }
  .header { display:flex; align-items:center; justify-content:space-between; border-bottom:3px solid ${accentColour}; padding-bottom:12px; margin-bottom:20px; }
  .header h1 { margin:0; font-size:20px; color:#1a1a1a; }
  .header .meta { font-size:10px; color:#666; text-align:right; }
  .badge { display:inline-block; padding:2px 6px; border-radius:3px; font-size:9px; font-weight:700; background:${accentColour}20; color:${accentColour}; }
  table { width:100%; border-collapse:collapse; }
  th { background:${accentColour}10; border-bottom:2px solid ${accentColour}40; padding:6px 8px; text-align:left; font-size:9px; text-transform:uppercase; letter-spacing:0.05em; color:#444; }
  td { padding:6px 8px; border-bottom:1px solid #eee; vertical-align:top; }
  tr:nth-child(even) td { background:#fafafa; }
  small { color:#666; font-size:9px; }
  .footer { margin-top:24px; font-size:9px; color:#999; border-top:1px solid #eee; padding-top:8px; }
</style>
</head><body>
<div class="header">
  <div>
    <h1>${title}</h1>
    <span class="badge">${productName}</span>
  </div>
  <div class="meta">Generated ${now}<br>${assets.length} asset${assets.length!==1?'s':''}</div>
</div>
<table>
<thead><tr>
  <th>Category</th><th>Asset</th><th>Expiry</th><th>Days</th><th>Cost</th><th>Auto-renew</th><th>Status</th>
  ${!isClientScope ? '<th>Client</th>' : ''}
  <th>Notes</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>
<div class="footer">Confidential — ${productName} · Generated ${now}</div>
</body></html>`;

  const blob = new Blob([html], { type:'text/html' });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url,'_blank');
  if (win) { win.focus(); setTimeout(()=>win.print(), 800); }
}

// ── Global Bulk Import Modal (client picker + BulkImportModal) ────────────────

function GlobalBulkImportModal({ onClose, onImported }) {
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null); // { slug, display_name }

  useEffect(() => {
    authFetch('/api/clients')
      .then(r => r.json())
      .then(d => {
        const active = (Array.isArray(d) ? d : []).filter(c => !c.archived);
        setClients(active);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (selected) {
    return (
      <BulkImportModal
        slug={selected.slug}
        onClose={onClose}
        onImported={() => { onImported(); }}
      />
    );
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px',overflowY:'auto'}}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:28,width:'100%',maxWidth:480}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
          <div style={{fontSize:18,fontWeight:800}}>Bulk Import to Assets Hub</div>
          <button className="btn btn-quiet btn-icon" onClick={onClose}>×</button>
        </div>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:20}}>
          Select a client to import their discovered or managed devices.
        </div>
        {loading
          ? <div style={{padding:24,textAlign:'center'}}><span className="spinner"/></div>
          : <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {clients.map(c => (
                <button key={c.slug} className="btn-card" onClick={() => setSelected(c)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{fontWeight:600}}>{c.display_name}</span>
                  <span style={{fontSize:11,color:'var(--text-muted)'}}>→</span>
                </button>
              ))}
              {clients.length === 0 && <div style={{color:'var(--text-muted)',fontSize:13}}>No clients found.</div>}
            </div>
        }
      </div>
    </div>
  );
}

// ── Main AssetHub Component ────────────────────────────────────────────────────

export default function AssetHub({ slug, isAdmin = false, showClient = false }) {
  const [assets,         setAssets]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [filter,         setFilter]         = useState({ category:'', status:'', visibility:'', search:'' });
  const [selected,       setSelected]       = useState(new Set());
  const [modal,          setModal]          = useState(null);
  const [delTarget,      setDelTarget]      = useState(null);
  const [bulkBusy,       setBulkBusy]       = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [productName] = useState(() => document.title.split(' ')[0] || 'Clearview');
  const [accentColour] = useState(() => {
    return getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#00d4ff';
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const url = showClient ? '/api/assets/global' : `/api/clients/${slug}/assets`;
      const res  = await authFetch(url);
      const data = await res.json();
      setAssets(data.assets || []);
    } catch(e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [slug, showClient]);

  useEffect(() => { load(); }, [load]);

  // Filtered assets
  const filtered = assets.filter(a => {
    if (filter.category && a.category !== filter.category) return false;
    if (filter.status   && a.status   !== filter.status)   return false;
    if (filter.visibility === 'visible'  && !a.visible_to_client) return false;
    if (filter.visibility === 'private'  &&  a.visible_to_client) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      if (![a.name,a.vendor,a.make,a.model,a.serial,a.hostname,a.owner_name,a.client_display_name].some(v=>(v||'').toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const allSelected  = filtered.length > 0 && filtered.every(a => selected.has(a.id));
  const someSelected = selected.size > 0;

  function toggleSelect(id, checked) {
    setSelected(prev => { const s = new Set(prev); checked ? s.add(id) : s.delete(id); return s; });
  }
  function selectAll(checked) {
    setSelected(checked ? new Set(filtered.map(a=>a.id)) : new Set());
  }

  async function bulkAction(action) {
    if (!someSelected) return;
    setBulkBusy(true);
    try {
      const url  = showClient ? '/api/assets/global/bulk' : `/api/clients/${slug}/assets/bulk`;
      const body = showClient
        ? { action, items: [...selected].map(id => ({ id, slug: assets.find(a=>a.id===id)?.client_slug })) }
        : { action, ids: [...selected] };
      await authFetch(url, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body) });
      setSelected(new Set());
      await load();
    } catch(e) { alert('Bulk action failed: ' + e); }
    finally { setBulkBusy(false); }
  }

  async function deleteAsset(asset) {
    try {
      await authFetch(`/api/clients/${asset.client_slug||slug}/assets/${asset.id}`, { method:'DELETE' });
      setDelTarget(null);
      await load();
    } catch(e) { alert('Delete failed: ' + e); }
  }

  function handleExportCSV() {
    if (showClient) {
      window.location.href = '/api/assets/global/export/csv';
    } else {
      window.location.href = `/api/clients/${slug}/assets/export/csv`;
    }
  }

  function handleExportClientCSV() {
    window.location.href = `/api/clients/${slug}/assets/export/csv?scope=client`;
  }

  function handleExportPDF() {
    const clientName = showClient ? 'All Clients' : (assets[0]?.client_display_name || slug);
    exportPDF(filtered, clientName, !showClient, productName, accentColour);
  }

  function handleExportClientPDF() {
    const clientAssets = filtered.filter(a => a.visible_to_client);
    const clientName   = assets[0]?.client_display_name || slug;
    exportPDF(clientAssets, clientName, true, productName, accentColour);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <div style={{padding:24,color:'var(--text-muted)',fontSize:13}}>Loading assets…</div>;
  if (error)   return <div style={{padding:24,color:'#ff2d55',fontSize:13}}>{error}</div>;

  return (
    <div>
      {/* Toolbar */}
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:12}}>
        <input className="input-sm"
          placeholder="Search assets…"
          value={filter.search}
          onChange={e=>setFilter(f=>({...f,search:e.target.value}))}
          style={{...inp,width:200,flex:'none'}}
        />
        <select className="input-sm" value={filter.category} onChange={e=>setFilter(f=>({...f,category:e.target.value}))} style={{...inp,width:'auto'}}>
          <option value="">All categories</option>
          {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select className="input-sm" value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} style={{...inp,width:'auto'}}>
          <option value="">All statuses</option>
          {STATUSES.map(s=><option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        {isAdmin && (
          <select className="input-sm" value={filter.visibility} onChange={e=>setFilter(f=>({...f,visibility:e.target.value}))} style={{...inp,width:'auto'}}>
            <option value="">All visibility</option>
            <option value="visible">Visible to client</option>
            <option value="private">Private</option>
          </select>
        )}

        <div style={{marginLeft:'auto',display:'flex',gap:6,flexWrap:'wrap'}}>
          {isAdmin && (
            <>
              {showClient && (
                <button className="btn btn-secondary btn-sm" onClick={()=>setShowBulkImport(true)}>
                  ⊕ Import
                </button>
              )}
              <div style={{position:'relative',display:'inline-block'}}>
                <ExportDropdown
                  label="Export"
                  options={[
                    { label:'Admin CSV (all)',      action: handleExportCSV },
                    { label:'Admin PDF (all)',      action: handleExportPDF },
                    ...(!showClient ? [
                      { label:'Client CSV (visible)', action: handleExportClientCSV },
                      { label:'Client PDF (visible)', action: handleExportClientPDF },
                    ] : []),
                  ]}
                />
              </div>
              <button className="btn btn-primary" onClick={()=>setModal('add')}>+ Add Asset</button>
            </>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {isAdmin && someSelected && (
        <div style={{
          display:'flex',gap:8,alignItems:'center',padding:'8px 12px',
          background:'rgba(0,212,255,0.06)',border:'1px solid var(--accent)',
          borderRadius:8,marginBottom:10,fontSize:12,
        }}>
          <span style={{fontWeight:600,color:'var(--accent)'}}>{selected.size} selected</span>
          <button className="btn btn-secondary btn-sm" onClick={()=>bulkAction('show')} disabled={bulkBusy}>Set visible</button>
          <button className="btn btn-secondary btn-sm" onClick={()=>bulkAction('hide')} disabled={bulkBusy}>Set private</button>
          <button className="btn btn-danger btn-sm" onClick={()=>bulkAction('delete')} disabled={bulkBusy}>Delete</button>
          <button className="btn btn-quiet btn-sm" style={{ marginLeft: 'auto' }} onClick={()=>setSelected(new Set())}>Clear</button>
        </div>
      )}

      {/* Table */}
      <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
        <ColHeader isAdmin={isAdmin} allSelected={allSelected} onSelectAll={selectAll}/>
        {filtered.length === 0
          ? <div style={{padding:24,textAlign:'center',color:'var(--text-muted)',fontSize:13}}>
              {assets.length === 0 ? 'No assets yet.' : 'No assets match the current filters.'}
            </div>
          : filtered.map(a => (
            <AssetRow
              key={a.id}
              asset={a}
              isAdmin={isAdmin}
              selected={selected.has(a.id)}
              onSelect={checked=>toggleSelect(a.id,checked)}
              onEdit={asset=>setModal(asset)}
              onDelete={setDelTarget}
              showClient={showClient}
            />
          ))
        }
      </div>

      <div style={{marginTop:8,fontSize:11,color:'var(--text-muted)'}}>
        {filtered.length} asset{filtered.length!==1?'s':''}{filtered.length!==assets.length?` of ${assets.length} total`:''}
      </div>

      {/* Global bulk import modal */}
      {showBulkImport && isAdmin && (
        <GlobalBulkImportModal
          onClose={()=>setShowBulkImport(false)}
          onImported={()=>{ setShowBulkImport(false); load(); }}
        />
      )}

      {/* Add/Edit modal */}
      {modal && (
        <AssetFormModal
          slug={modal === 'add' ? slug : (modal.client_slug || slug)}
          asset={modal === 'add' ? null : modal}
          onSaved={()=>{ setModal(null); load(); }}
          onClose={()=>setModal(null)}
        />
      )}

      {/* Delete confirmation */}
      {delTarget && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'var(--surface)',borderRadius:12,padding:24,maxWidth:400,width:'100%',border:'1px solid var(--border)'}}>
            <div style={{fontWeight:700,fontSize:15,marginBottom:8}}>Remove asset?</div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:20}}>
              <strong>{delTarget.name}</strong> will be permanently removed. This cannot be undone.
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-secondary" onClick={()=>setDelTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>deleteAsset(delTarget)}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Export Dropdown ────────────────────────────────────────────────────────────

function ExportDropdown({ label, options }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{position:'relative'}}>
      <button className="btn btn-secondary btn-sm" onClick={()=>setOpen(o=>!o)}>
        {label} ▾
      </button>
      {open && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:200}} onClick={()=>setOpen(false)}/>
          <div style={{
            position:'absolute',right:0,top:'100%',marginTop:4,
            background:'var(--surface)',border:'1px solid var(--border)',
            borderRadius:8,padding:4,zIndex:201,minWidth:180,
            boxShadow:'0 4px 16px rgba(0,0,0,0.25)',
          }}>
            {options.map((o,i) => (
              <button key={i} className="menu-item" onClick={()=>{ setOpen(false); o.action(); }}>
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}